import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ClipboardCheck, CalendarDays, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  scheduled: { label: "Scheduled", tone: "border-indigo-500/40 text-indigo-300 bg-indigo-500/10" },
  passed:    { label: "Passed",    tone: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" },
  failed:    { label: "Failed",    tone: "border-rose-500/40 text-rose-300 bg-rose-500/10" },
  cancelled: { label: "Cancelled", tone: "border-zinc-500/40 text-zinc-400 bg-white/[0.03]" },
};

const TYPES = ["fire safety", "elevator", "pool", "roof", "boiler", "unit walk-through", "annual", "insurance", "other"];

const empty = { area: "", type: "fire safety", inspector: "", scheduled_for: new Date().toISOString().slice(0, 16), notes: "" };

export default function InspectionsPage() {
  const { propertyId } = useProperty();
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!propertyId) return;
    try {
      const { data } = await api.get("/inspections", { params: { property_id: propertyId } });
      setItems(data.items || []);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load"); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (i) => {
    setEditing(i);
    setForm({
      area: i.area || "", type: i.type || "other", inspector: i.inspector || "",
      scheduled_for: (i.scheduled_for || "").slice(0, 16),
      notes: i.notes || "",
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/inspections/${editing.id}`, form);
        toast.success("Inspection updated");
      } else {
        await api.post("/inspections", { ...form, property_id: propertyId }, { params: { property_id: propertyId } });
        toast.success("Inspection scheduled");
      }
      setDialogOpen(false);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Save failed"); }
    finally { setBusy(false); }
  };

  const setStatus = async (i, status) => {
    try {
      await api.patch(`/inspections/${i.id}`, { status });
      toast.success(`Marked ${status}`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Update failed"); }
  };

  const remove = async (i) => {
    if (!window.confirm(`Delete inspection "${i.area}"?`)) return;
    try {
      await api.delete(`/inspections/${i.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Delete failed"); }
  };

  return (
    <div>
      <PageHeader eyebrow="Compliance" title="Inspections" subtitle="Schedule, pass, fail, and follow up on every inspection required for the community." />

      <div className="flex items-center gap-2 mb-3">
        <Button onClick={openCreate} className="bg-orange-500 hover:bg-orange-600 rounded-full" data-testid="inspection-add-btn">
          <Plus className="h-4 w-4 mr-1" /> Schedule inspection
        </Button>
        <div className="ml-auto text-xs text-zinc-500">{items.length} inspection{items.length === 1 ? "" : "s"}</div>
      </div>

      <div className="space-y-3">
        {items.map((i) => {
          const s = STATUS[i.status] || STATUS.scheduled;
          return (
            <Card key={i.id} className="border-white/10 bg-card p-4" data-testid={`inspection-row-${i.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className="h-9 w-9 rounded-md bg-orange-500/10 border border-orange-500/30 flex items-center justify-center shrink-0">
                    <ClipboardCheck className="h-4 w-4 text-orange-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium text-zinc-100">{i.area}</div>
                      <Badge variant="outline" className={s.tone}>{s.label}</Badge>
                    </div>
                    <div className="text-xs text-zinc-500">{i.type} · {i.inspector || "unassigned"}</div>
                    <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {new Date(i.scheduled_for).toLocaleString()}</div>
                    {i.notes && <div className="text-xs text-zinc-400 mt-2 whitespace-pre-wrap">{i.notes}</div>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Select value={i.status} onValueChange={(v) => setStatus(i, v)}>
                    <SelectTrigger className="w-32 h-8 bg-white/[0.04] border-white/10 text-xs" data-testid={`inspection-status-${i.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                      {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(i)} className="h-8 text-zinc-400" data-testid={`inspection-edit-${i.id}`}>
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(i)} className="h-8 text-zinc-400 hover:text-red-300" data-testid={`inspection-delete-${i.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
        {items.length === 0 && (
          <Card className="p-8 text-center border-white/10" data-testid="inspections-empty">
            <ClipboardCheck className="h-6 w-6 mx-auto text-zinc-500 mb-2" />
            <div className="text-sm text-zinc-400">No inspections scheduled. Click <b>Schedule inspection</b> to add one.</div>
          </Card>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[520px]" data-testid="inspection-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-orange-400" /> {editing ? "Edit inspection" : "Schedule inspection"}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">Track every compliance touchpoint your community needs — schedule, pass, fail with notes.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div>
              <Label className="text-xs text-zinc-400">Area / location</Label>
              <Input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })}
                     placeholder="Boiler room, pool deck, unit 12B…" className="bg-white/[0.04] border-white/10" data-testid="inspection-area" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-zinc-400">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="bg-white/[0.04] border-white/10" data-testid="inspection-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                    {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Inspector</Label>
                <Input value={form.inspector} onChange={(e) => setForm({ ...form, inspector: e.target.value })}
                       placeholder="Name" className="bg-white/[0.04] border-white/10" data-testid="inspection-inspector" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Scheduled for</Label>
              <Input type="datetime-local" value={form.scheduled_for}
                     onChange={(e) => setForm({ ...form, scheduled_for: e.target.value })}
                     className="bg-white/[0.04] border-white/10" data-testid="inspection-when" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Notes</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        placeholder="Any deficiencies, follow-ups, or context…" className="bg-white/[0.04] border-white/10 text-sm" data-testid="inspection-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={busy} className="border-white/10">Cancel</Button>
            <Button onClick={submit} disabled={busy || !form.area} className="bg-orange-500 hover:bg-orange-600" data-testid="inspection-save-btn">
              {busy ? "Saving…" : editing ? "Save" : "Schedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
